// Global packages and components
import slugify from 'slugify'
import { clsx } from 'clsx'
import { useState, useEffect } from 'react'
import { XMarkIcon, ChevronLeftIcon } from '@heroicons/react/24/outline'
import {
  Checkbox,
  FilterLink,
  FilterPrice,
  PriceRangeFilter,
} from '~/components'
import { formatAttributeFilters, getCustomFilterLabel } from '~/utils'

// Types
import type {
  BrendaSearchFilter,
  ProductListingPriceValues,
  FilterLabel,
} from '~/types'

type KlevuFilter = {
  key: string
  values: string[]
  settings?: {
    singleSelect?: boolean
  }
}

interface Props {
  open: boolean
  filters: BrendaSearchFilter[]
  attributes: string[]
  priceValues: ProductListingPriceValues
  priceLimit: BrendaSearchFilter | null
  filterLabels?: FilterLabel[]
  setOpen: (e: boolean) => void
  setPriceValues: (e: ProductListingPriceValues) => void
  setQueryAttributes: (e: string) => void
}

// Main export
const OffcanvasFiltersNav = ({
  open,
  filters,
  attributes,
  priceValues,
  priceLimit,
  filterLabels,
  setOpen,
  setPriceValues,
  setQueryAttributes,
}: Props) => {
  const [activeAttributes, setActiveAttributes] = useState<KlevuFilter[]>([])
  const [openPanel, setOpenPanel] = useState<string | null>(null)

  const closeAll = () => {
    setOpen(false)

    const timeout = setTimeout(() => {
      setOpenPanel(null)
    }, 700)

    return () => {
      clearTimeout(timeout)
    }
  }

  // Format the active attributes data
  useEffect(() => {
    setActiveAttributes(attributes ? formatAttributeFilters(attributes) : [])
  }, [attributes])

  // Auto close when price field changed
  useEffect(() => {
    if (priceLimit) {
      setOpenPanel(null)
    }
  }, [priceLimit])

  return (
    <div
      className={clsx(
        'fixed left-0 top-0 z-50 flex h-dvh w-full flex-col bg-white shadow-md transition-transform duration-500 lg:hidden',
        {
          'translate-y-0': open,
          'translate-y-full': !open,
        }
      )}
    >
      <div className="flex items-center justify-between border-b border-teal-green bg-gray-100 px-8 pb-3 pt-8">
        <div>
          {openPanel ? (
            <button
              onClick={() => setOpenPanel(null)}
              className="flex items-center text-teal-green"
            >
              <ChevronLeftIcon className="mr-2 size-4" />
              <span className="pt-0.5 font-bold underline">{openPanel}</span>
            </button>
          ) : (
            <p className="mb-0 font-bold text-teal-green">Filter</p>
          )}
        </div>
        <div>
          <button
            className="flex size-8 items-center justify-center rounded-full bg-gray-200"
            onClick={closeAll}
          >
            <XMarkIcon className="size-5" />
          </button>
        </div>
      </div>
      {(filters || priceLimit) && (
        <ul
          className={clsx('relative grid h-auto w-full py-2', {
            'overflow-y-auto overflow-x-hidden': openPanel == null,
          })}
        >
          {priceLimit && (
            <li key="parent-price">
              <FilterPrice
                title="Price (£)"
                hasArrow
                onClick={() => setOpenPanel('Price')}
                className="block w-full px-8 py-4"
                min={priceLimit.min}
                max={priceLimit.max}
                start={priceLimit.start}
                end={priceLimit.end}
              />
              <ul
                className={clsx(
                  'absolute right-0 top-0 z-[2] h-full w-full bg-white py-2 transition-transform duration-500',
                  {
                    'translate-x-0': openPanel == 'Price',
                    'translate-x-full': openPanel !== 'Price',
                  }
                )}
              >
                <div className="block w-full px-8 py-4">
                  <PriceRangeFilter
                    {...priceValues}
                    setPriceValues={setPriceValues}
                  />
                </div>
              </ul>
            </li>
          )}
          {filters.map((filter, pi) => (
            <li key={'parent' + pi}>
              <FilterLink
                title={filter.label}
                name={filter.key}
                hasArrow
                activeAttributes={activeAttributes}
                onClick={() => setOpenPanel(filter.label)}
                className="block w-full px-8 py-4"
                filterLabels={filterLabels}
              />
              {filter?.options && (
                <ul
                  className={clsx(
                    'absolute right-0 top-0 z-[2] h-full w-full bg-white py-2 transition-transform duration-500',
                    {
                      'translate-x-0': openPanel == filter.label,
                      'translate-x-full': openPanel !== filter.label,
                    }
                  )}
                >
                  {filter.options.map((option, ci) => {
                    const customLabel = getCustomFilterLabel(
                      filter.key,
                      option.value,
                      filterLabels
                    )

                    return (
                      <li
                        key={'child-' + ci}
                        className="flex w-full items-center justify-between px-8 py-4"
                      >
                        <Checkbox
                          id={`filter-${slugify(filter.key + '-' + option.value)}`}
                          label={customLabel ?? option.value}
                          checked={attributes.some(
                            e => e == encodeURI(filter.key + '|' + option.value)
                          )}
                          onChange={() => {
                            setQueryAttributes(
                              encodeURI(filter.key + '|' + option.value)
                            )
                            setOpenPanel(null)
                          }}
                        />
                      </li>
                    )
                  })}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default OffcanvasFiltersNav
